import { useState, useMemo } from 'react';
import { Plus, Grid3X3, Users, Clock, MoreHorizontal } from 'lucide-react';
import { useMultiGrid } from '../../hooks/useMultiGrid';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { YearSelector } from '../common/YearSelector';
import { CloneGridsToYear } from './CloneGridsToYear';
import { StaffAllocationGrid } from '../../types/grid';
import { Staff } from '../../types/data';

interface MultiGridTabsProps {
  staff?: Staff[]; // Optional - kept for backward compatibility
  onTabChange?: (gridId: string | null) => void;
  onCreateGrid?: () => void;
  onEditGrid?: (gridId: string) => void;
  className?: string;
}

interface TabProps {
  grid: StaffAllocationGrid;
  isActive: boolean;
  onClick: () => void;
  onClose?: () => void;
  onEdit?: () => void;
}

function GridTab({ grid, isActive, onClick, onClose, onEdit }: TabProps) {
  const [showActions, setShowActions] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);


  const gridStats = useMemo(() => {
    const totalClasses = grid.classes.length;
    const totalLines = grid.lines?.length || 0;
    const assignedClasses = grid.classes.filter(c => 
      Object.values(c.lineAssignments || {}).some(teacher => teacher)
    ).length;
    const uniqueTeachers = new Set(
      Object.values(grid.classes.flatMap(c => Object.values(c.lineAssignments || {})))
        .filter(Boolean)
    ).size;

    return {
      totalClasses,
      totalLines,
      assignedClasses,
      uniqueTeachers,
      completionPercentage: totalClasses > 0 ? Math.round((assignedClasses / totalClasses) * 100) : 0
    };
  }, [grid.classes, grid.lines]);

  return (
    <div 
      className={`relative flex items-center px-4 py-2 border-b-2 cursor-pointer transition-all duration-200 ${
        isActive 
          ? 'border-navy-500 bg-navy-50 text-navy-700' 
          : 'border-transparent hover:bg-ink-50 text-ink-700'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center min-w-0">
        <Grid3X3 size={16} className="mr-2 flex-shrink-0" />
        
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{grid.name}</span>
            {grid.defaultClassMinutes && (
              <span className="font-mono tabular-nums px-2 py-0.5 bg-ink-100 text-ink-500 text-xs rounded flex-shrink-0">
                {grid.defaultClassMinutes}min classes
              </span>
            )}
            {!grid.isActive && (
              <span className="px-2 py-0.5 bg-danger-bg text-danger-fg text-xs rounded flex-shrink-0">
                Inactive
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3 text-xs text-ink-500 mt-1">
            <span className="font-mono tabular-nums flex items-center gap-1">
              <Grid3X3 size={12} />
              {gridStats.totalClasses}
            </span>
            <span className="font-mono tabular-nums flex items-center gap-1">
              <Users size={12} />
              {gridStats.uniqueTeachers}
            </span>
            <span className="font-mono tabular-nums flex items-center gap-1">
              <Clock size={12} />
              {gridStats.completionPercentage}%
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center ml-2 gap-1">
        <div className="relative">
          <button
            ref={setButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
            className="p-1 rounded hover:bg-ink-200 text-ink-400"
          >
            <MoreHorizontal size={14} />
          </button>
          
          {showActions && buttonRef && (
            <>
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowActions(false)}
              />
              <div 
                className="fixed bg-white border rounded-md shadow-lg z-50 min-w-32"
                style={{
                  top: buttonRef.getBoundingClientRect().bottom + 4,
                  right: window.innerWidth - buttonRef.getBoundingClientRect().right,
                }}
              >
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-ink-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.();
                    setShowActions(false);
                  }}
                >
                  Edit Grid
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-ink-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle clone action
                    setShowActions(false);
                  }}
                >
                  Clone Grid
                </button>
                {onClose && (
                  <button
                    className="w-full text-left px-3 py-2 text-sm hover:bg-ink-100 text-danger-fg"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                      setShowActions(false);
                    }}
                  >
                    Delete Grid
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function MultiGridTabs({ onTabChange, onCreateGrid, onEditGrid, className = '' }: MultiGridTabsProps) {
  const {
    gridsForYear,
    currentGridId,
    setCurrentGridId,
    multiGridView,
    setMultiGridView,
    deleteGrid,
    isLoading
  } = useMultiGrid();
  const confirm = useConfirm();

  // Get working year from context
  let workingYear: number;
  try {
    const academicYearContext = useAcademicYear();
    workingYear = academicYearContext.workingYear;
  } catch (e) {
    workingYear = new Date().getFullYear();
  }

  const [showCloneToYear, setShowCloneToYear] = useState(false);

  // Use gridsForYear instead of allGrids for filtering
  const activeGrids = gridsForYear.filter(grid => grid.isActive);
  
  const combinedStats = useMemo(() => {
    const totalClasses = activeGrids.reduce((sum, grid) => sum + grid.classes.length, 0);
    const totalTeachers = new Set(
      activeGrids.flatMap(grid => 
        Object.values(grid.classes.flatMap(c => Object.values(c.lineAssignments || {})))
      ).filter(Boolean)
    ).size;

    return { totalClasses, totalTeachers, gridCount: activeGrids.length };
  }, [activeGrids]);

  const handleTabClick = (gridId: string) => {
    setCurrentGridId(gridId);
    // When clicking on an individual grid tab, update activeGridIds based on view mode
    if (multiGridView.viewMode === 'tabs') {
      // In tabs mode, only the clicked grid should be active
      setMultiGridView({
        ...multiGridView,
        activeGridIds: [gridId]
      });
    } else {
      // In combined/comparison mode, ensure this grid is included in active grids
      const currentActiveGrids = multiGridView.activeGridIds || [];
      if (!currentActiveGrids.includes(gridId)) {
        setMultiGridView({
          ...multiGridView,
          activeGridIds: [...currentActiveGrids, gridId]
        });
      }
    }
    onTabChange?.(gridId);
  };

  const handleCombinedViewClick = () => {
    setCurrentGridId(null);
    // When clicking "All Grids", ensure all active grids are in activeGridIds
    const allActiveGridIds = activeGrids.map(g => g.id);
    setMultiGridView({
      ...multiGridView,
      activeGridIds: allActiveGridIds
    });
    onTabChange?.(null);
  };


  const handleDeleteGrid = async (gridId: string) => {
    const ok = await confirm({
      title: 'Delete grid?',
      message: 'Are you sure you want to delete this grid? This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (ok) {
      await deleteGrid(gridId);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white border-b ${className}`}>
        <div className="flex items-center px-4 py-3">
          <div className="animate-pulse bg-ink-200 h-6 w-32 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border-b ${className}`}>
      <div className="flex items-center overflow-x-auto scrollbar-hide">
        {/* Year Selector */}
        <div className="flex-shrink-0 px-2 border-r border-ink-200">
          <YearSelector
            showGridCount={true}
            onStartNewYear={() => setShowCloneToYear(true)}
          />
        </div>

        {/* Combined View Tab */}
        {activeGrids.length > 1 && (
          <div 
            className={`flex items-center px-4 py-2 border-b-2 cursor-pointer transition-all duration-200 flex-shrink-0 ${
              currentGridId === null
                ? 'border-navy-500 bg-navy-50 text-navy-700' 
                : 'border-transparent hover:bg-ink-50 text-ink-700'
            }`}
            onClick={handleCombinedViewClick}
          >
            <div className="flex items-center">
              <div className="w-4 h-4 mr-2 bg-navy-500 rounded-sm flex-shrink-0"></div>
              <div>
                <div className="font-medium">All Grids</div>
                <div className="flex items-center gap-3 text-xs text-ink-500 mt-1">
                  <span className="font-mono tabular-nums flex items-center gap-1">
                    <Grid3X3 size={12} />
                    {combinedStats.totalClasses}
                  </span>
                  <span className="font-mono tabular-nums flex items-center gap-1">
                    <Users size={12} />
                    {combinedStats.totalTeachers}
                  </span>
                  <span className="font-mono tabular-nums text-xs text-navy-600">
                    {combinedStats.gridCount} grids
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Individual Grid Tabs */}
        {activeGrids.map(grid => (
          <div key={grid.id} className="flex-shrink-0">
            <GridTab
              grid={grid}
              isActive={currentGridId === grid.id}
              onClick={() => handleTabClick(grid.id)}
              onClose={activeGrids.length > 1 ? () => handleDeleteGrid(grid.id) : undefined}
              onEdit={() => onEditGrid?.(grid.id)}
            />
          </div>
        ))}

        {/* Add Grid Button */}
        <button
          onClick={() => {
            onCreateGrid?.();
          }}
          className="flex items-center px-3 py-2 text-ink-500 hover:text-ink-700 hover:bg-ink-50 border-b-2 border-transparent flex-shrink-0"
        >
          <Plus size={16} className="mr-1" />
          <span className="text-sm">Add Grid</span>
        </button>
      </div>

      {/* View Mode Selector */}
      {activeGrids.length > 1 && (
        <div className="px-4 py-2 bg-ink-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  // For tabs mode, keep current selection if any
                  setMultiGridView({
                    ...multiGridView,
                    viewMode: 'tabs',
                    activeGridIds: multiGridView.activeGridIds || []
                  });
                }}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  multiGridView.viewMode === 'tabs'
                    ? 'bg-navy-100 text-navy-700'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                Tabs
              </button>
              <button
                onClick={() => {
                  // For combined mode, ensure we have multiple grids selected
                  const currentActiveGrids = multiGridView.activeGridIds || [];
                  const allActiveGridIds = activeGrids.map(g => g.id);

                  setMultiGridView({
                    ...multiGridView,
                    viewMode: 'combined',
                    activeGridIds: currentActiveGrids.length > 1 ? currentActiveGrids : allActiveGridIds
                  });
                }}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  multiGridView.viewMode === 'combined'
                    ? 'bg-navy-100 text-navy-700'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                Combined
              </button>
              <button
                onClick={() => {
                  // For comparison mode, ensure we have multiple grids selected
                  const currentActiveGrids = multiGridView.activeGridIds || [];
                  const allActiveGridIds = activeGrids.map(g => g.id);

                  setMultiGridView({
                    ...multiGridView,
                    viewMode: 'comparison',
                    activeGridIds: currentActiveGrids.length > 1 ? currentActiveGrids : allActiveGridIds
                  });
                }}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  multiGridView.viewMode === 'comparison'
                    ? 'bg-navy-100 text-navy-700'
                    : 'text-ink-500 hover:text-ink-900'
                }`}
              >
                Compare
              </button>
            </div>
            
            <div className="text-sm text-ink-500">
              {activeGrids.length} grids available for {workingYear}
            </div>
          </div>
        </div>
      )}

      {/* Clone Grids to Year Modal */}
      <CloneGridsToYear
        isOpen={showCloneToYear}
        onClose={() => setShowCloneToYear(false)}
      />
    </div>
  );
}